import { useEffect } from "react"

import { useNavigate } from "react-router-dom"

import { LoadingSection } from "@/components/loading-section"
import { logger } from "@/lib/logger"

import { MSAL_LOGIN_START_KEY, RETURN_TO_KEY } from "../utils/auth-constants"
import { guardAuthLoop } from "../utils/auth-loop-guard"

function normalizeReturnTo(returnTo: string | null): string {
  if (!returnTo) return "/"

  // Prevent landing back on the redirect handler
  if (returnTo.startsWith("/redirect")) return "/"

  // Accept relative app paths; fall back to root for anything suspicious.
  if (returnTo.startsWith("/")) return returnTo

  // If an absolute URL is present, fall back to full-page navigation.
  try {
    const url = new URL(returnTo)
    return url.pathname + url.search + url.hash
  } catch {
    return "/"
  }
}

export function AuthRedirectPage() {
  const navigate = useNavigate()

  useEffect(() => {
    // MicrosoftAuthProvider already handles `handleRedirectPromise()` during initialization,
    // so by the time this page renders, the redirect has been processed and the active account
    // has been set (if any). We just need to navigate to the return URL.

    const returnToValue = sessionStorage.getItem(RETURN_TO_KEY) ?? "/"
    if (guardAuthLoop("msal", returnToValue)) {
      // Loop detected; guardAuthLoop already redirected to /auth/error
      return
    }

    // Clear the redirect-in-progress flag. Loop guard attempts are NOT cleared here;
    // they expire naturally after 2 minutes so that repeated failures are still detected.
    sessionStorage.removeItem(MSAL_LOGIN_START_KEY)

    const returnTo = normalizeReturnTo(sessionStorage.getItem(RETURN_TO_KEY))
    sessionStorage.removeItem(RETURN_TO_KEY)

    logger.info("AuthRedirectPage navigating to return URL", { returnTo })
    void navigate(returnTo, { replace: true })
  }, [navigate])

  return (
    <section className="flex h-screen w-screen items-center justify-center">
      <div className="flex w-full max-w-md flex-col items-center gap-4 px-4 text-center">
        <h1 className="text-xl font-semibold">Signing you in…</h1>
        <p className="text-muted-foreground text-sm">
          Please wait while we finish the authentication process.
        </p>
        <div className="w-full">
          <LoadingSection />
        </div>
      </div>
    </section>
  )
}

AuthRedirectPage.displayName = "AuthRedirectPage"
